/**
 * Rendering of placed widgets, shared by the maps.
 *
 * A land tells this module where it is (its content box on the canvas) and
 * the module turns each placement's fractions into a pixel position. SVG
 * widgets go inside the map's SVG layer; HTML widgets (mascots, notes, toys)
 * go in the map's HTML layer, both through the two helpers below. HTML seats
 * let clicks pass through to the map unless the widget is interactive. A
 * link widget's picture is wrapped in a button that opens the roadmap's link
 * callout on the first press and follows the link on the second. Each
 * placement has its own error boundary: a widget that throws is left out
 * and logged once, and the rest of the map still draws.
 */
import {
  RoadmapLinkCallout,
  roadmapInAppPath,
  roadmapLinkTriggerProps,
  useRoadmapLinkCallout,
} from 'course/splash/components/RoadmapLinkCallout';
import {PartErrorBoundary} from 'components/errors/PartErrorBoundary';
import {useCallback, type ReactNode} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import styled, {css} from 'styled-components';
import {IS_WEBKIT} from '../../platform/browserEngine';
import type {Placement} from './lookAndFeelSchema';
import {
  widgetById,
  type WidgetDefinition,
  type WidgetRenderContext,
} from './widgets';

/**
 * CSS for a map's canvas. In WebKit it turns off the felt widgets' fuzz
 * filter: WebKit redraws SVG filters on the CPU on every scroll and
 * animation frame, and a map full of felt widgets kept Safari busy enough to
 * blank the page. Other browsers keep the fuzzy edge.
 */
export const mapCanvasFilterCss = IS_WEBKIT
  ? css`
      [filter$='-fuzz)'] {
        filter: none;
      }
    `
  : css``;

/**
 * A land's box on the canvas, as the maps report it. The content box runs
 * from just under the header to the horizon, which is the range placement
 * fractions map onto.
 */
export interface LandBox {
  /** The week id the land shows. */
  id: string;
  /** Top of the land on the canvas. */
  top: number;
  /** Top of the placeable area, just under the header. */
  contentTop: number;
  /** Bottom of the land (its horizon). */
  bottom: number;
  /** The land's accent colour. */
  accent: string;
  /** The land's ground colour. */
  ground: string;
  /** The land's title as shown. */
  title: string;
  /** The coins on this land, by item id, at their centres on the canvas. */
  stones: Array<{id: string; x: number; y: number}>;
  /**
   * The scenery and mascots the map draws on its own for this land, as
   * placements, so an editor can adopt them into the document.
   */
  autoPlacements: Placement[];
}

/** The map's geometry, for editors that overlay on it. */
export interface RoadmapLayoutInfo {
  /** Logical canvas width, the same on every screen. */
  width: number;
  /** Logical canvas height. */
  height: number;
  /**
   * How the canvas is scaled on screen: 1 where it fits, less on screens
   * narrower than the canvas.
   */
  scale: number;
  lands: LandBox[];
}

/**
 * Turns a placement into a canvas position.
 *
 * @param placement - The placement.
 * @param land - The land it belongs to.
 * @param width - Canvas width in pixels.
 * @return The widget's base point in canvas pixels.
 */
export function placementPoint(
  placement: Placement,
  land: LandBox,
  width: number,
): {x: number; y: number} {
  return {
    x: placement.x * width,
    y: land.contentTop + placement.y * (land.bottom - land.contentTop),
  };
}

/**
 * The inverse of {@link placementPoint}: where a canvas point falls within a
 * land, clamped to [0, 1] on both axes.
 *
 * @param point - A canvas point in pixels.
 * @param land - The land containing it.
 * @param width - Canvas width in pixels.
 * @return Placement fractions.
 */
export function placementFractions(
  point: {x: number; y: number},
  land: LandBox,
  width: number,
): {x: number; y: number} {
  const span = Math.max(1, land.bottom - land.contentTop);
  return {
    x: Math.min(1, Math.max(0, point.x / width)),
    y: Math.min(1, Math.max(0, (point.y - land.contentTop) / span)),
  };
}

/**
 * Draws one widget. The widget's render runs here rather than in the map's
 * own render, so the boundary around each placement catches a widget that
 * throws and the rest of the map still draws.
 *
 * @param props.widget - The widget.
 * @param props.context - What the widget draws with.
 * @return The widget's picture.
 * @throws Whatever the widget's render throws, for the boundary to catch.
 */
function DrawnWidget({
  widget,
  context,
}: {
  widget: WidgetDefinition;
  context: WidgetRenderContext;
}) {
  return <>{widget.render(context)}</>;
}

/**
 * The SVG widgets placed on a land, ready to drop into the map's SVG layer.
 *
 * @param args.placements - The land's placements.
 * @param args.land - The land's box.
 * @param args.width - Canvas width in pixels.
 * @param args.isLocked - Fade the widgets for a locked land.
 * @return SVG fragments, one group per placement.
 */
export function svgDecorations({
  placements,
  land,
  width,
  isLocked,
}: {
  placements: readonly Placement[];
  land: LandBox;
  width: number;
  isLocked: boolean;
}): ReactNode {
  return (
    <g opacity={isLocked ? 0.45 : 1}>
      {placements.map(placement => {
        const widget = widgetById(placement.widget);
        if (!widget || widget.kind !== 'svg') {
          return null;
        }
        const {x, y} = placementPoint(placement, land, width);
        return (
          <PartErrorBoundary
            key={placement.id}
            area={`map-widget:${placement.widget}`}
          >
            <g
              transform={`translate(${x} ${y}) scale(${placement.flip ? -placement.scale : placement.scale} ${placement.scale})`}
            >
              <DrawnWidget
                widget={widget}
                context={{
                  ground: land.ground,
                  accent: land.accent,
                  scale: placement.scale,
                  variant: placement.variant,
                  text: placement.text,
                }}
              />
            </g>
          </PartErrorBoundary>
        );
      })}
    </g>
  );
}

const HtmlSeat = styled.div<{
  $x: number;
  $y: number;
  $flip: boolean;
  $isLocked: boolean;
  $isInteractive: boolean;
}>`
  position: absolute;
  left: ${({$x}) => $x}px;
  top: ${({$y}) => $y}px;
  transform: translate(-50%, -100%) ${({$flip}) => ($flip ? 'scaleX(-1)' : '')};
  pointer-events: ${({$isInteractive}) => ($isInteractive ? 'auto' : 'none')};
  filter: ${({$isLocked}) =>
    $isLocked ? 'saturate(0.15) opacity(0.6)' : 'none'};
`;

/** Space between a link widget's base and its callout. */
const CALLOUT_GAP = 8;

const LinkButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;

  & > svg {
    display: block;
    transition: transform 0.15s ease-out;
  }

  &:active > svg {
    transform: scale(0.94);
  }

  &:focus-visible {
    outline: 2px dashed #8c1515;
    outline-offset: 4px;
    border-radius: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    & > svg {
      transition: none;
    }
  }
`;

/**
 * The DOM id key for a link widget's callout, kept apart from roadmap item
 * ids so the two kinds of callout never share an id.
 *
 * @param placementId - The placement's id.
 * @return The key to pass as the callout's item id.
 * @throws Never.
 */
export function linkWidgetCalloutKey(placementId: string): string {
  return `widget-${placementId}`;
}

/**
 * The HTML widgets placed on a land (mascots, notes, toys and link
 * widgets), positioned by their bottom centre. A link widget's callout is
 * drawn beside its seat rather than inside it, so a flipped widget never
 * mirrors the callout's text.
 *
 * @param props.placements - The land's placements.
 * @param props.land - The land's box.
 * @param props.width - Canvas width in pixels.
 * @param props.isLocked - Fade the widgets for a locked land.
 * @return The positioned widgets.
 * @throws Never.
 */
export function HtmlDecorations({
  placements,
  land,
  width,
  isLocked,
}: {
  placements: readonly Placement[];
  land: LandBox;
  width: number;
  isLocked: boolean;
}) {
  const {courseId = ''} = useParams<{courseId: string}>();
  const navigate = useNavigate();
  const {openId, press, close} = useRoadmapLinkCallout();

  const follow = useCallback(
    (href: string) => {
      const path = roadmapInAppPath(href);
      if (path === null) {
        window.open(href, '_blank', 'noopener');
        return;
      }
      navigate(path);
    },
    [navigate],
  );

  const renderWidget = (widget: WidgetDefinition, placement: Placement) => (
    <DrawnWidget
      widget={widget}
      context={{
        ground: land.ground,
        accent: land.accent,
        scale: placement.scale,
        variant: placement.variant,
        text: placement.text,
      }}
    />
  );

  return (
    <>
      {placements.map(placement => {
        const widget = widgetById(placement.widget);
        if (!widget || widget.kind !== 'html') {
          return null;
        }
        const {x, y} = placementPoint(placement, land, width);
        const seat = (children: ReactNode) => (
          <HtmlSeat
            $x={x}
            $y={y}
            $flip={placement.flip}
            $isLocked={isLocked}
            $isInteractive={
              (widget.isInteractive ?? false) || Boolean(widget.link)
            }
          >
            {children}
          </HtmlSeat>
        );
        const {link} = widget;
        const area = `map-widget:${placement.widget}`;
        if (!link) {
          return (
            <PartErrorBoundary key={placement.id} area={area}>
              {seat(renderWidget(widget, placement))}
            </PartErrorBoundary>
          );
        }
        const calloutKey = linkWidgetCalloutKey(placement.id);
        const href = link.href({courseId});
        const isOpen = openId === calloutKey;
        return (
          <PartErrorBoundary key={placement.id} area={area}>
            {seat(
              <LinkButton
                type="button"
                aria-label={link.title}
                {...roadmapLinkTriggerProps({itemId: calloutKey, isOpen})}
                onClick={event => {
                  press({
                    id: calloutKey,
                    trigger: event.currentTarget,
                    follow: () => follow(href),
                  });
                }}
              >
                {renderWidget(widget, placement)}
              </LinkButton>,
            )}
            {isOpen && (
              <RoadmapLinkCallout
                itemId={calloutKey}
                title={link.title}
                description={link.description}
                href={href}
                centerX={x}
                top={y + CALLOUT_GAP}
                boundsWidth={width}
                onFollow={() => {
                  close();
                  follow(href);
                }}
                onClose={close}
              />
            )}
          </PartErrorBoundary>
        );
      })}
    </>
  );
}
